import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  CircularProgress,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';

// Icons
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
  ReceiptLong as ReceiptLongIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  LocalAtm as LocalAtmIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import { walletService, MOCK_WALLET_DATA } from '../services/walletService';

export default function UserWallet({ onNavigate, isEmbedded = false }) {
  // State management
  const [walletData, setWalletData] = useState(MOCK_WALLET_DATA);
  const [transactions, setTransactions] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Payment Account state
  const [paymentAccount, setPaymentAccount] = useState(null);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accType, setAccType] = useState('BANK');
  const [accHolderName, setAccHolderName] = useState('');
  const [accNumber, setAccNumber] = useState('');
  const [accIfsc, setAccIfsc] = useState('');
  const [accBankName, setAccBankName] = useState('');
  const [accUpiId, setAccUpiId] = useState('');
  const [accountSubmitting, setAccountSubmitting] = useState(false);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('ALL'); // ALL, CREDIT, DEBIT, PENDING
  const [dateFilter, setDateFilter] = useState('ALL'); // ALL, THIS_MONTH, LAST_30_DAYS

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modals state
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('UPI');
  const [withdrawTarget, setWithdrawTarget] = useState('');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  // Bank Transfer Form Fields state
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');

  const [addMoneyModalOpen, setAddMoneyModalOpen] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('UPI / GPay');
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastOpen, setToastOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));

  // Fetch wallet data
  const loadWalletInfo = async (isRefresh = false, pageNum = page, pageSize = rowsPerPage) => {
    if (!isLoggedIn) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const summary = await walletService.getWalletSummary();
      const txnResult = await walletService.getTransactions(pageNum, pageSize);
      const accResult = await walletService.getPaymentAccount();
      if (summary) setWalletData(summary);
      if (accResult) setPaymentAccount(accResult);
      if (txnResult) {
        setTransactions(txnResult.content || []);
        setTotalElements(txnResult.totalElements || 0);
        setTotalPages(txnResult.totalPages || 1);
      }
      if (isRefresh) {
        showToast('Wallet refreshed successfully!', 'info');
      }
    } catch (err) {
      console.error('Failed to load wallet:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadWalletInfo(false, page, rowsPerPage);
    }
  }, [isLoggedIn, page, rowsPerPage]);

  const showToast = (message, severity = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Copy helper
  const handleCopy = (text, id) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    setCopiedId(id);
    showToast(`Copied details to clipboard!`, 'info');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Open Payment Account Modal
  const openPaymentAccountModal = () => {
    if (paymentAccount) {
      setAccType(paymentAccount.type || 'BANK');
      setAccHolderName(paymentAccount.accountHolderName || '');
      setAccNumber(paymentAccount.accountNumber || '');
      setAccIfsc(paymentAccount.ifscCode || '');
      setAccBankName(paymentAccount.bankName || '');
      setAccUpiId(paymentAccount.upiId || '');
    } else {
      setAccType('BANK');
      setAccHolderName('');
      setAccNumber('');
      setAccIfsc('');
      setAccBankName('');
      setAccUpiId('');
    }
    setAccountModalOpen(true);
  };

  // Handle Payment Account Save (POST for create, PUT for update)
  const handleSavePaymentAccountSubmit = async (e) => {
    e.preventDefault();
    if (!accHolderName.trim()) {
      showToast('Please enter Account Holder Name', 'error');
      return;
    }
    if (accType === 'BANK') {
      if (!accBankName.trim()) {
        showToast('Please enter Bank Name', 'error');
        return;
      }
      if (!accNumber.trim()) {
        showToast('Please enter Account Number', 'error');
        return;
      }
      if (!accIfsc.trim()) {
        showToast('Please enter IFSC Code', 'error');
        return;
      }
    } else if (accType === 'UPI') {
      if (!accUpiId.trim()) {
        showToast('Please enter UPI ID', 'error');
        return;
      }
    }

    setAccountSubmitting(true);
    try {
      const payload = {
        type: accType || 'BANK',
        accountHolderName: accHolderName.trim(),
        accountNumber: accNumber.trim() || null,
        ifscCode: accIfsc.trim().toUpperCase() || null,
        bankName: accBankName.trim() || null,
        upiId: accUpiId.trim() || null,
      };

      let res;
      if (paymentAccount && paymentAccount.id) {
        payload.id = paymentAccount.id;
        // PUT API - {{URL}}/api/payment-accounts
        res = await walletService.updatePaymentAccount(payload);
      } else {
        // POST API - {{URL}}/api/payment-accounts
        res = await walletService.addPaymentAccount(payload);
      }

      if (res && res.success) {
        const updated = res.data || payload;
        setPaymentAccount(updated);
        showToast(res.message || 'Payment account saved successfully!', 'success');
        setAccountModalOpen(false);
      }
    } catch (err) {
      showToast(err.message || 'Failed to save payment account details', 'error');
    } finally {
      setAccountSubmitting(false);
    }
  };

  // Handle Open Withdraw Funds Modal (prepopulate with saved payment account details)
  const handleOpenWithdrawModal = () => {
    if (paymentAccount) {
      const defaultMethod = paymentAccount.type || 'UPI';
      setWithdrawMethod(defaultMethod);
      if (paymentAccount.accountHolderName) setAccHolderName(paymentAccount.accountHolderName);
      if (paymentAccount.bankName) setBankName(paymentAccount.bankName);
      if (paymentAccount.accountNumber) {
        setAccountNumber(paymentAccount.accountNumber);
        setConfirmAccountNumber(paymentAccount.accountNumber);
      }
      if (paymentAccount.ifscCode) setIfscCode(paymentAccount.ifscCode);
      if (paymentAccount.upiId) setWithdrawTarget(paymentAccount.upiId);
    } else {
      setWithdrawMethod('UPI');
      setAccHolderName('');
      setBankName('');
      setAccountNumber('');
      setConfirmAccountNumber('');
      setIfscCode('');
      setWithdrawTarget('');
    }
    setWithdrawModalOpen(true);
  };

  // Handle switching Withdrawal Method dropdown in modal
  const handleWithdrawMethodChange = (method) => {
    setWithdrawMethod(method);
    if (paymentAccount) {
      if (paymentAccount.accountHolderName) setAccHolderName(paymentAccount.accountHolderName);
      if (method === 'BANK') {
        if (paymentAccount.bankName) setBankName(paymentAccount.bankName);
        if (paymentAccount.accountNumber) {
          setAccountNumber(paymentAccount.accountNumber);
          setConfirmAccountNumber(paymentAccount.accountNumber);
        }
        if (paymentAccount.ifscCode) setIfscCode(paymentAccount.ifscCode);
      } else if (method === 'UPI') {
        if (paymentAccount.upiId) setWithdrawTarget(paymentAccount.upiId);
      }
    }
  };

  // Filtered transactions computation (latest record first)
  const filteredTransactions = useMemo(() => {
    const list = transactions.filter((txn) => {
      // Type filter
      if (filterType === 'CREDIT' && txn.direction !== 'CREDIT') return false;
      if (filterType === 'DEBIT' && txn.direction !== 'DEBIT') return false;
      if (filterType === 'PENDING' && txn.status !== 'PENDING') return false;

      // Date filter
      if (dateFilter !== 'ALL') {
        const rawDate = txn.createdAt || txn.date;
        const txnDate = rawDate ? new Date(rawDate) : new Date();
        const now = new Date();
        if (dateFilter === 'THIS_MONTH') {
          if (txnDate.getMonth() !== now.getMonth() || txnDate.getFullYear() !== now.getFullYear()) {
            return false;
          }
        } else if (dateFilter === 'LAST_30_DAYS') {
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (txnDate < thirtyDaysAgo) return false;
        }
      }

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = txn.title?.toLowerCase().includes(q);
        const matchesSubtitle = txn.subtitle?.toLowerCase().includes(q);
        const matchesId = String(txn.id)?.toLowerCase().includes(q);
        const matchesRef = (txn.referenceId || txn.reference)?.toLowerCase().includes(q);
        const matchesMerchant = txn.merchantName?.toLowerCase().includes(q);

        return matchesTitle || matchesSubtitle || matchesId || matchesRef || matchesMerchant;
      }

      return true;
    });

    // Sort descending (latest record first based on createdAt / date / id)
    return list.sort((a, b) => {
      const timeA = new Date(a.createdAt || a.date || 0).getTime();
      const timeB = new Date(b.createdAt || b.date || 0).getTime();
      if (!isNaN(timeA) && !isNaN(timeB) && timeA !== timeB) {
        return timeB - timeA;
      }
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [transactions, filterType, dateFilter, searchQuery]);

  // Handle Withdraw Submission
  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    const numAmount = parseFloat(withdrawAmount);
    if (!numAmount || numAmount <= 0) {
      showToast('Please enter a valid withdrawal amount', 'error');
      return;
    }
    if (numAmount > walletData.balance) {
      showToast('Insufficient wallet balance!', 'error');
      return;
    }

    if (!accHolderName.trim()) {
      showToast('Please enter Account Holder Name', 'error');
      return;
    }

    if (withdrawMethod === 'BANK') {
      if (!bankName.trim()) {
        showToast('Please enter Bank Name', 'error');
        return;
      }
      if (!accountNumber.trim()) {
        showToast('Please enter Account Number', 'error');
        return;
      }
      if (!confirmAccountNumber.trim()) {
        showToast('Please confirm Account Number', 'error');
        return;
      }
      if (accountNumber.trim() !== confirmAccountNumber.trim()) {
        showToast('Account numbers do not match!', 'error');
        return;
      }
      if (!ifscCode.trim()) {
        showToast('Please enter IFSC Code', 'error');
        return;
      }
    } else {
      if (!withdrawTarget.trim()) {
        showToast('Please enter a valid UPI ID', 'error');
        return;
      }
    }

    setWithdrawSubmitting(true);
    try {
      // Save or update payment account details if not available or if updated details entered
      if (!paymentAccount) {
        const newAccountPayload = {
          type: withdrawMethod,
          accountHolderName: accHolderName.trim(),
          accountNumber: withdrawMethod === 'BANK' ? accountNumber.trim() : null,
          ifscCode: withdrawMethod === 'BANK' ? ifscCode.trim().toUpperCase() : null,
          bankName: withdrawMethod === 'BANK' ? bankName.trim() : null,
          upiId: withdrawMethod === 'UPI' ? withdrawTarget.trim() : null,
        };

        const saveAccRes = await walletService.addPaymentAccount(newAccountPayload);
        if (saveAccRes && (saveAccRes.success || saveAccRes.data)) {
          const savedData = saveAccRes.data || newAccountPayload;
          setPaymentAccount(savedData);
        }
      } else if (withdrawMethod === 'UPI' && withdrawTarget.trim() && (paymentAccount.upiId !== withdrawTarget.trim() || paymentAccount.accountHolderName !== accHolderName.trim())) {
        const updateAccountPayload = {
          ...paymentAccount,
          type: 'UPI',
          accountHolderName: accHolderName.trim(),
          upiId: withdrawTarget.trim(),
        };
        const updateAccRes = await walletService.updatePaymentAccount(updateAccountPayload);
        if (updateAccRes && (updateAccRes.success || updateAccRes.data)) {
          const updatedData = updateAccRes.data || updateAccountPayload;
          setPaymentAccount(updatedData);
        }
      } else if (withdrawMethod === 'BANK' && (paymentAccount.accountHolderName !== accHolderName.trim() || paymentAccount.accountNumber !== accountNumber.trim() || paymentAccount.bankName !== bankName.trim() || paymentAccount.ifscCode !== ifscCode.trim().toUpperCase())) {
        const updateAccountPayload = {
          ...paymentAccount,
          type: 'BANK',
          accountHolderName: accHolderName.trim(),
          accountNumber: accountNumber.trim(),
          bankName: bankName.trim(),
          ifscCode: ifscCode.trim().toUpperCase(),
        };
        const updateAccRes = await walletService.updatePaymentAccount(updateAccountPayload);
        if (updateAccRes && (updateAccRes.success || updateAccRes.data)) {
          const updatedData = updateAccRes.data || updateAccountPayload;
          setPaymentAccount(updatedData);
        }
      }

      // API call POST /api/wallet/withdraw with payload { "amount": numAmount }
      const res = await walletService.withdrawFunds(numAmount);

      if (res && res.success) {
        showToast(res.message || `₹${numAmount.toFixed(2)} withdrawn successfully!`, 'success');
        setWithdrawModalOpen(false);
        setWithdrawAmount('');

        // Refresh transaction table & wallet info from server API, resetting pagination to page 0
        setPage(0);
        await loadWalletInfo(false, 0, rowsPerPage);
      }
    } catch (error) {
      showToast(error.message || 'Withdrawal failed. Please try again.', 'error');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  // Handle Add Money Submission
  const handleAddMoneySubmit = async (e) => {
    e.preventDefault();
    const numAmount = parseFloat(addAmount);
    if (!numAmount || numAmount <= 0) {
      showToast('Please enter a valid deposit amount', 'error');
      return;
    }

    setAddSubmitting(true);
    try {
      const res = await walletService.addMoney(numAmount, paymentMethod);

      if (res && res.success) {
        setWalletData((prev) => ({
          ...prev,
          balance: prev.balance + numAmount,
        }));

        if (res.transaction) {
          setTransactions((prev) => [res.transaction, ...prev]);
        }

        showToast(res.message || `₹${numAmount.toFixed(2)} added to wallet successfully!`, 'success');
        setAddMoneyModalOpen(false);
        setAddAmount('');
      }
    } catch (error) {
      showToast(error.message || 'Deposit failed. Please try again.', 'error');
    } finally {
      setAddSubmitting(false);
    }
  };

  // Status Chip Renderer
  const renderStatusChip = (status) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'SETTLED':
      case 'COMPLETED':
      case 'SUCCESS':
        return (
          <Chip
            size="small"
            icon={<CheckCircleIcon style={{ fontSize: 14, color: '#4CAF50' }} />}
            label="Settled"
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.12)',
              color: '#4CAF50',
              fontWeight: 600,
              fontSize: '11px',
              border: '1px solid rgba(76, 175, 80, 0.3)',
            }}
          />
        );
      case 'PENDING':
        return (
          <Chip
            size="small"
            icon={<AccessTimeIcon style={{ fontSize: 14, color: '#FFB74D' }} />}
            label="Pending"
            sx={{
              backgroundColor: 'rgba(255, 183, 77, 0.12)',
              color: '#FFB74D',
              fontWeight: 600,
              fontSize: '11px',
              border: '1px solid rgba(255, 183, 77, 0.3)',
            }}
          />
        );
      case 'CANCELLED':
        return (
          <Chip
            size="small"
            icon={<CloseIcon style={{ fontSize: 14, color: '#E4572E' }} />}
            label="Cancelled"
            sx={{
              backgroundColor: 'rgba(228, 87, 46, 0.12)',
              color: '#E4572E',
              fontWeight: 600,
              fontSize: '11px',
              border: '1px solid rgba(228, 87, 46, 0.3)',
            }}
          />
        );
      case 'FAILED':
        return (
          <Chip
            size="small"
            icon={<CloseIcon style={{ fontSize: 14, color: '#F44336' }} />}
            label="Failed"
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.12)',
              color: '#F44336',
              fontWeight: 600,
              fontSize: '11px',
              border: '1px solid rgba(244, 67, 54, 0.3)',
            }}
          />
        );
      default:
        return <Chip size="small" label={status || 'N/A'} sx={{ color: '#F5F1E6', backgroundColor: 'rgba(245, 241, 230, 0.1)' }} />;
    }
  };

  // Transaction Icon Renderer
  const renderTxnIcon = (type, direction) => {
    if (direction === 'DEBIT' || type === 'WITHDRAWAL') {
      return (
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            backgroundColor: 'rgba(228, 87, 46, 0.15)',
            color: '#E4572E',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <ArrowUpwardIcon fontSize="small" />
        </Box>
      );
    }
    if (type === 'DEPOSIT') {
      return (
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            backgroundColor: 'rgba(47, 143, 118, 0.18)',
            color: '#2F8F76',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <TrendingUpIcon fontSize="small" />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '10px',
          backgroundColor: 'rgba(212, 162, 76, 0.18)',
          color: '#D4A24C',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <ArrowDownwardIcon fontSize="small" />
      </Box>
    );
  };

  if (!isLoggedIn) {
    return (
      <Box
        component="section"
        id="user-wallet-auth-required"
        sx={{
          padding: { xs: '60px 0 100px', md: '100px 0 140px' },
          minHeight: '80vh',
          background: '#0F2E2B',
          color: '#F5F1E6',
          display: 'flex',
          alignItems: 'center',
          justify: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              borderRadius: '28px',
              backgroundColor: '#153A35',
              border: '1px solid rgba(212, 162, 76, 0.3)',
              textAlign: 'center',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: 'rgba(212, 162, 76, 0.15)',
                color: '#D4A24C',
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: 3,
                border: '1px solid rgba(212, 162, 76, 0.3)',
              }}
            >
              <LockIcon sx={{ fontSize: 36 }} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: '#F5F1E6',
                fontSize: { xs: '24px', sm: '28px' },
                mb: 1.5,
              }}
            >
              Wallet Access Restricted
            </Typography>

            <Typography
              sx={{
                color: 'rgba(245, 241, 230, 0.7)',
                fontSize: '15px',
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Please log in to your account to view your available cashback balance, track transaction receipts, and request instant bank withdrawals.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => onNavigate && onNavigate('login')}
                startIcon={<PersonIcon />}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 700,
                  fontSize: '15px',
                  px: 4,
                  py: 1.4,
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: '#E8C078',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Sign In to Access Wallet
              </Button>

              <Button
                variant="outlined"
                onClick={() => onNavigate && onNavigate('signup')}
                sx={{
                  borderColor: 'rgba(245, 241, 230, 0.3)',
                  color: '#F5F1E6',
                  fontWeight: 600,
                  fontSize: '15px',
                  px: 3,
                  py: 1.4,
                  borderRadius: '14px',
                  '&:hover': {
                    borderColor: '#D4A24C',
                    color: '#D4A24C',
                    backgroundColor: 'rgba(212, 162, 76, 0.08)',
                  },
                }}
              >
                Create Account
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      id="user-wallet"
      sx={{
        padding: isEmbedded ? '40px 0' : { xs: '30px 0 80px', md: '50px 0 100px' },
        minHeight: '80vh',
        background: '#0F2E2B',
        color: '#F5F1E6',
      }}
    >
      <Container maxWidth="lg">
        {/* Navigation / Header Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: { xs: '26px', md: '34px' },
                  color: '#F5F1E6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <AccountBalanceWalletIcon sx={{ color: '#D4A24C', fontSize: '32px' }} />
                User Wallet & Ledger
              </Typography>
              <Typography sx={{ color: 'rgba(245, 241, 230, 0.65)', fontSize: '14.5px', mt: 0.5 }}>
                Track your real-time balance, coupon cashback settlements, and instant withdrawals.
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            <Chip
              icon={<CheckCircleIcon style={{ fontSize: 14, color: '#D4A24C' }} />}
              label="KYC Verified"
              variant="outlined"
              sx={{
                borderColor: 'rgba(212, 162, 76, 0.4)',
                color: '#E8C078',
                backgroundColor: 'rgba(212, 162, 76, 0.08)',
                fontWeight: 600,
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => loadWalletInfo(true)}
              disabled={loading || refreshing}
              startIcon={
                refreshing ? (
                  <CircularProgress size={14} thickness={4} sx={{ color: '#D4A24C' }} />
                ) : (
                  <RefreshIcon />
                )
              }
              sx={{
                borderColor: 'rgba(245, 241, 230, 0.2)',
                color: '#F5F1E6',
                borderRadius: '10px',
                px: 2,
                '&:hover': {
                  borderColor: '#D4A24C',
                  color: '#D4A24C',
                  backgroundColor: 'rgba(212, 162, 76, 0.08)',
                },
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Box>

        {/* Top Summary Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Main Wallet Balance Card */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                padding: { xs: 3, sm: 3.5 },
                borderRadius: '24px',
                background: 'linear-gradient(145deg, #153A35 0%, #102C28 100%)',
                border: '1px solid rgba(212, 162, 76, 0.3)',
                boxShadow: '0 12px 36px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(212, 162, 76, 0.15) 0%, transparent 70%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '12px',
                      letterSpacing: '0.08em',
                      color: '#E8C078',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}
                  >
                    Available Wallet Balance
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '12px',
                      color: 'rgba(245, 241, 230, 0.5)',
                    }}
                  >
                    {walletData.accountNo || '●●●● 4821'}
                  </Typography>
                </Box>

                {loading || refreshing ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: { xs: 52, sm: 64 },
                      my: 1,
                    }}
                  >
                    <CircularProgress size={32} thickness={4} sx={{ color: '#D4A24C' }} />
                  </Box>
                ) : (
                  <Typography
                    variant="h2"
                    sx={{
                      fontFamily: '"Fraunces", serif',
                      fontWeight: 800,
                      fontSize: { xs: '36px', sm: '46px' },
                      color: '#D4A24C',
                      letterSpacing: '-0.02em',
                      my: 1,
                    }}
                  >
                    ₹{Number(walletData?.balance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                )}

                <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.6)' }}>
                  Linked UPI ID:{' '}
                  <Box
                    component="span"
                    onClick={() => handleCopy(walletData.upiId, 'upi')}
                    sx={{
                      color: '#F5F1E6',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textDecoration: 'underline dotted',
                      '&:hover': { color: '#E8C078' },
                    }}
                  >
                    {walletData.upiId}
                  </Box>
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Grid
                container
                spacing={2}
                sx={{ mt: 3.5 }}
              >
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleOpenWithdrawModal}
                    startIcon={<AccountBalanceIcon />}
                    sx={{
                      backgroundColor: '#D4A24C',
                      color: '#0F2E2B',
                      fontWeight: 700,
                      fontSize: '14px',
                      py: 1.2,
                      borderRadius: '14px',
                      '&:hover': {
                        backgroundColor: '#E8C078',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    Withdraw Funds
                  </Button>
                </Grid>



                {onNavigate && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => onNavigate('coupons')}
                      startIcon={<ConfirmationNumberIcon />}
                      sx={{
                        color: '#E8C078',
                        fontWeight: 600,
                        fontSize: '13.5px',
                        py: 1.2,
                        '&:hover': {
                          backgroundColor: 'rgba(232, 192, 120, 0.08)',
                        },
                      }}
                    >
                      Redeem Vouchers
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Payment Account Details Card (Beside Wallet Balance Card) */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                padding: { xs: 3, sm: 3.5 },
                borderRadius: '24px',
                background: 'linear-gradient(145deg, #153A35 0%, #102C28 100%)',
                border: '1px solid rgba(212, 162, 76, 0.3)',
                boxShadow: '0 12px 36px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '12px',
                      letterSpacing: '0.08em',
                      color: '#E8C078',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}
                  >
                    Payment Account Details
                  </Typography>
                  <Chip
                    size="small"
                    label={paymentAccount ? (paymentAccount.type || 'BANK') : 'NOT LINKED'}
                    sx={{
                      backgroundColor: paymentAccount ? 'rgba(212, 162, 76, 0.15)' : 'rgba(255, 183, 77, 0.15)',
                      color: paymentAccount ? '#D4A24C' : '#FFB74D',
                      fontWeight: 700,
                      fontSize: '11px',
                      border: paymentAccount ? '1px solid rgba(212, 162, 76, 0.3)' : '1px solid rgba(255, 183, 77, 0.3)',
                    }}
                  />
                </Box>

                {paymentAccount ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        color: '#F5F1E6',
                        fontSize: { xs: '20px', sm: '24px' },
                        mb: 1.5,
                      }}
                    >
                      {paymentAccount.accountHolderName || 'Account Holder'}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)' }}>Bank Name</Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#E8C078' }}>
                          {paymentAccount.bankName || 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)' }}>Account Number</Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '14px', fontWeight: 600, color: '#F5F1E6' }}>
                          {paymentAccount.accountNumber || 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)' }}>IFSC Code</Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '13.5px', color: '#4CAF50', fontWeight: 600 }}>
                          {paymentAccount.ifscCode || 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)' }}>UPI ID</Typography>
                        <Typography sx={{ fontSize: '13.5px', color: '#F5F1E6', fontWeight: 600 }}>
                          {paymentAccount.upiId || 'Not Linked'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 3,
                      px: 2,
                      textAlign: 'center',
                      borderRadius: '16px',
                      backgroundColor: 'rgba(15, 46, 43, 0.4)',
                      border: '1px dashed rgba(212, 162, 76, 0.25)',
                      my: 1,
                    }}
                  >
                    <AccountBalanceIcon sx={{ fontSize: 36, color: 'rgba(212, 162, 76, 0.5)', mb: 1 }} />
                    <Typography sx={{ color: '#F5F1E6', fontWeight: 700, fontSize: '15px' }}>
                      No Payment Account Linked
                    </Typography>
                    <Typography sx={{ color: 'rgba(245, 241, 230, 0.6)', fontSize: '12.5px', mt: 0.5 }}>
                      Add your bank transfer or UPI details for direct cashback cashouts.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Edit / Add Action */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={openPaymentAccountModal}
                  startIcon={<AccountBalanceIcon />}
                  sx={{
                    borderColor: 'rgba(212, 162, 76, 0.4)',
                    color: '#E8C078',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    px: 3,
                    py: 1.1,
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: 'rgba(212, 162, 76, 0.12)',
                      borderColor: '#D4A24C',
                    },
                  }}
                >
                  {paymentAccount ? 'Edit Account Details' : 'Add Payment Account'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Quick Metrics Cards Row (4 Columns) */}
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2.5}>
              {/* Total Cashback Earned */}
              <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '18px',
                    backgroundColor: '#153A35',
                    border: '1px solid rgba(245, 241, 230, 0.12)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justify: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.65)', fontWeight: 500 }}>
                      Total Cashback Earned
                    </Typography>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(76, 175, 80, 0.15)',
                        color: '#4CAF50',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <TrendingUpIcon fontSize="small" />
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1.5 }}>
                    {loading || refreshing ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', height: 36 }}>
                        <CircularProgress size={22} thickness={4} sx={{ color: '#4CAF50' }} />
                      </Box>
                    ) : (
                      <Typography
                        variant="h5"
                        sx={{
                          fontFamily: '"Fraunces", serif',
                          fontWeight: 700,
                          color: '#F5F1E6',
                          fontSize: '24px',
                        }}
                      >
                        ₹{Number(walletData?.totalCashback ?? walletData?.totalEarnings ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '11.5px', color: '#4CAF50', mt: 0.5, fontWeight: 600 }}>
                      Lifetime cashback
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Pending Cashback */}
              <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '18px',
                    backgroundColor: '#153A35',
                    border: '1px solid rgba(245, 241, 230, 0.12)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justify: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.65)', fontWeight: 500 }}>
                      Pending Settlement
                    </Typography>
                    <Tooltip title="Cashback entries awaiting verification">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 183, 77, 0.15)',
                          color: '#FFB74D',
                          display: 'grid',
                          placeItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <AccessTimeIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  </Box>

                  <Box sx={{ mt: 1.5 }}>
                    {loading || refreshing ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', height: 36 }}>
                        <CircularProgress size={22} thickness={4} sx={{ color: '#FFB74D' }} />
                      </Box>
                    ) : (
                      <Typography
                        variant="h5"
                        sx={{
                          fontFamily: '"Fraunces", serif',
                          fontWeight: 700,
                          color: '#FFB74D',
                          fontSize: '24px',
                        }}
                      >
                        ₹{Number(walletData?.totalPendingAmount ?? walletData?.pendingCashback ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)', mt: 0.5 }}>
                      Clears automatically
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Total Ledger Count */}
              <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '18px',
                    backgroundColor: '#153A35',
                    border: '1px solid rgba(245, 241, 230, 0.12)',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.65)', fontWeight: 500 }}>
                      Ledger Lines
                    </Typography>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(212, 162, 76, 0.15)',
                        color: '#D4A24C',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <ReceiptLongIcon fontSize="small" />
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1.5 }}>
                    {loading || refreshing ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', height: 36 }}>
                        <CircularProgress size={22} thickness={4} sx={{ color: '#D4A24C' }} />
                      </Box>
                    ) : (
                      <Typography
                        variant="h5"
                        sx={{
                          fontFamily: '"Fraunces", serif',
                          fontWeight: 700,
                          color: '#F5F1E6',
                          fontSize: '24px',
                        }}
                      >
                        {walletData?.totalTransactions ?? totalElements ?? transactions.length} Records
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '11.5px', color: 'rgba(245, 241, 230, 0.5)', mt: 0.5 }}>
                      Fully audited transactions
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Transaction History & Filter Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '20px',
            backgroundColor: '#153A35',
            border: '1px solid rgba(245, 241, 230, 0.14)',
            overflow: 'hidden',
          }}
        >
          {/* Header Controls */}
          <Box
            sx={{
              p: { xs: 2.5, md: 3 },
              borderBottom: '1px solid rgba(245, 241, 230, 0.12)',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#F5F1E6',
                }}
              >
                Transaction History
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.6)' }}>
                {loading || refreshing
                  ? 'Updating transaction records...'
                  : `Showing ${filteredTransactions.length} of ${totalElements || transactions.length} wallet entries`}
              </Typography>
            </Box>

            {/* Controls: Search + Filter Tabs */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search Txn ID, merchant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(245, 241, 230, 0.5)', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <CloseIcon sx={{ fontSize: 16, color: 'rgba(245, 241, 230, 0.5)' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: { xs: '100%', sm: 220 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(15, 46, 43, 0.6)',
                    borderRadius: '12px',
                    color: '#F5F1E6',
                    fontSize: '13.5px',
                    '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.18)' },
                    '&:hover fieldset': { borderColor: '#D4A24C' },
                  },
                }}
              />

              {/* Type Filter Buttons */}
              <Stack direction="row" spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' }, overflowX: 'auto' }}>
                {[
                  { label: 'All', value: 'ALL' },
                  { label: 'Credits (+)', value: 'CREDIT' },
                  { label: 'Debits (-)', value: 'DEBIT' },
                  { label: 'Pending', value: 'PENDING' },
                ].map((item) => (
                  <Button
                    key={item.value}
                    size="small"
                    onClick={() => setFilterType(item.value)}
                    sx={{
                      fontSize: '12.5px',
                      py: 0.6,
                      px: 1.6,
                      borderRadius: '10px',
                      whiteSpace: 'nowrap',
                      backgroundColor:
                        filterType === item.value ? '#D4A24C' : 'rgba(245, 241, 230, 0.05)',
                      color: filterType === item.value ? '#0F2E2B' : 'rgba(245, 241, 230, 0.7)',
                      fontWeight: filterType === item.value ? 700 : 500,
                      '&:hover': {
                        backgroundColor: filterType === item.value ? '#E8C078' : 'rgba(245, 241, 230, 0.12)',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Box>

          {/* Transactions Table */}
          <TableContainer>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(15, 46, 43, 0.8)' }}>
                  <TableCell sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}>
                    DATE & TIME
                  </TableCell>
                  <TableCell sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}>
                    TRANSACTION DETAILS
                  </TableCell>
                  <TableCell sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}>
                    REF / TXN ID
                  </TableCell>
                  <TableCell sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}>
                    CATEGORY
                  </TableCell>
                  <TableCell sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}>
                    STATUS
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}
                  >
                    AMOUNT
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: '#E8C078', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}
                  >
                    RECEIPT
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading || refreshing ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={36} thickness={4} sx={{ color: '#D4A24C' }} />
                      <Typography sx={{ mt: 1.5, color: 'rgba(245, 241, 230, 0.7)', fontSize: '14px', fontWeight: 500 }}>
                        Loading transaction ledger...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <ReceiptLongIcon sx={{ fontSize: 44, color: 'rgba(245, 241, 230, 0.2)', mb: 1 }} />
                      <Typography sx={{ color: '#F5F1E6', fontWeight: 600, fontSize: '16px' }}>
                        No transactions found
                      </Typography>
                      <Typography sx={{ color: 'rgba(245, 241, 230, 0.5)', fontSize: '13px', mt: 0.5 }}>
                        {searchQuery || filterType !== 'ALL' || dateFilter !== 'ALL'
                          ? 'No transaction records match your search criteria or filters.'
                          : 'You do not have any transaction history yet.'}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => loadWalletInfo(true)}
                          disabled={loading || refreshing}
                          startIcon={
                            refreshing ? (
                              <CircularProgress size={14} thickness={4} sx={{ color: '#D4A24C' }} />
                            ) : (
                              <RefreshIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          sx={{
                            borderColor: 'rgba(212, 162, 76, 0.3)',
                            color: '#E8C078',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            px: 2,
                            py: 0.6,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#D4A24C',
                              backgroundColor: 'rgba(212, 162, 76, 0.08)',
                            },
                          }}
                        >
                          {refreshing ? 'Refreshing...' : 'Refresh Transactions'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((txn) => {
                    const isCredit = txn.direction === 'CREDIT';
                    const rawDateVal = txn.createdAt || txn.date;
                    const isValidDate = rawDateVal && !isNaN(new Date(rawDateVal).getTime());
                    const dateObj = isValidDate ? new Date(rawDateVal) : new Date();

                    const formattedDate = dateObj.toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    });
                    const formattedTime = dateObj.toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    });

                    return (
                      <TableRow
                        key={txn.id}
                        hover
                        sx={{
                          borderBottom: '1px solid rgba(245, 241, 230, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 241, 230, 0.03)',
                          },
                        }}
                      >
                        {/* Date & Time */}
                        <TableCell sx={{ color: 'rgba(245, 241, 230, 0.85)', py: 2 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#F5F1E6' }}>
                            {formattedDate}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: 'rgba(245, 241, 230, 0.5)' }}>
                            {formattedTime}
                          </Typography>
                        </TableCell>

                        {/* Transaction Details */}
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {renderTxnIcon(txn.type, txn.direction)}
                            <Box>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#F5F1E6' }}>
                                {txn.title}
                              </Typography>
                              <Typography sx={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.55)' }}>
                                {txn.subtitle || txn.merchantName}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Ref / Txn ID */}
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              sx={{
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: '12px',
                                color: '#E8C078',
                                fontWeight: 500,
                              }}
                            >
                              {txn.referenceId || txn.reference || txn.id}
                            </Typography>
                            <Tooltip title="Copy Reference ID">
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(txn.referenceId || txn.reference || txn.id, txn.id)}
                                sx={{ color: 'rgba(245, 241, 230, 0.4)', p: 0.3 }}
                              >
                                <ContentCopyIcon style={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Category */}
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            size="small"
                            label={txn.type || 'LEDGER'}
                            sx={{
                              backgroundColor: 'rgba(245, 241, 230, 0.08)',
                              color: 'rgba(245, 241, 230, 0.8)',
                              fontSize: '11px',
                              fontWeight: 500,
                              borderRadius: '6px',
                            }}
                          />
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 2 }}>{renderStatusChip(txn.status)}</TableCell>

                        {/* Amount */}
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '14.5px',
                              fontWeight: 700,
                              color:
                                String(txn.status || '').toUpperCase() === 'PENDING'
                                  ? '#FFB74D'
                                  : String(txn.status || '').toUpperCase() === 'FAILED' ||
                                    String(txn.status || '').toUpperCase() === 'CANCELLED'
                                    ? '#E4572E'
                                    : '#4CAF50',
                            }}
                          >
                            {isCredit ? '+' : ''}₹
                            {Number(txn.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>

                        {/* Receipt Action */}
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Tooltip title="View Digital Receipt">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedTxn(txn);
                                setReceiptModalOpen(true);
                              }}
                              sx={{
                                color: '#FFFFFF',
                                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  color: '#FFFFFF',
                                  backgroundColor: 'rgba(255, 255, 255, 0.24)',
                                  borderColor: '#FFFFFF',
                                },
                              }}
                            >
                              <ReceiptLongIcon style={{ fontSize: 16, color: '#FFFFFF' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination Controls */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            component="div"
            count={totalElements || filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: 'rgba(245, 241, 230, 0.7)',
              borderTop: '1px solid rgba(245, 241, 230, 0.1)',
              '.MuiTablePagination-selectIcon': { color: 'rgba(245, 241, 230, 0.7)' },
            }}
          />
        </Paper>
      </Container>

      {/* WITHDRAWAL MODAL */}
      <Dialog
        open={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#153A35',
            color: '#F5F1E6',
            borderRadius: '24px',
            border: '1px solid rgba(245, 241, 230, 0.14)',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            fontFamily: '"Fraunces", serif',
            fontSize: '22px',
          }}
        >
          Withdraw Funds
          <IconButton onClick={() => setWithdrawModalOpen(false)} sx={{ color: 'rgba(245, 241, 230, 0.6)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleWithdrawSubmit}>
          <DialogContent>
            <Box sx={{ mb: 2.5, p: 2, borderRadius: '14px', backgroundColor: 'rgba(15, 46, 43, 0.6)', border: '1px solid rgba(212, 162, 76, 0.2)' }}>
              <Typography sx={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)' }}>Available to Withdraw</Typography>
              <Typography variant="h5" sx={{ fontFamily: '"Fraunces", serif', color: '#D4A24C', fontWeight: 700 }}>
                ₹{Number(walletData?.balance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, mb: 1 }}>Amount to Cashout (₹)</Typography>
            <TextField
              fullWidth
              placeholder="e.g. 500"
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#0F2E2B',
                  color: '#F5F1E6',
                  borderRadius: '12px',
                  '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(245, 241, 230, 0.7)' }}>Withdrawal Method</InputLabel>
              <Select
                value={withdrawMethod}
                onChange={(e) => handleWithdrawMethodChange(e.target.value)}
                label="Withdrawal Method"
                sx={{
                  backgroundColor: '#0F2E2B',
                  color: '#F5F1E6',
                  borderRadius: '12px',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4A24C' },
                  '.MuiSelect-icon': { color: '#F5F1E6' },
                }}
              >
                <MenuItem value="UPI">UPI Handle (GPay / PhonePe / Paytm)</MenuItem>
                <MenuItem value="BANK">Bank Account Transfer (NEFT/IMPS)</MenuItem>
              </Select>
            </FormControl>

            {withdrawMethod === 'BANK' ? (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  placeholder="e.g. First Last Name"
                  value={accHolderName}
                  onChange={(e) => setAccHolderName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
                <TextField
                  fullWidth
                  label="Bank Name"
                  placeholder="e.g. HDFC Bank, SBI, ICICI"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
                <TextField
                  fullWidth
                  label="Account Number"
                  placeholder="Enter bank account number"
                  type="password"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Account Number"
                  placeholder="Re-enter bank account number"
                  value={confirmAccountNumber}
                  onChange={(e) => setConfirmAccountNumber(e.target.value)}
                  error={!!confirmAccountNumber && accountNumber !== confirmAccountNumber}
                  helperText={
                    confirmAccountNumber && accountNumber !== confirmAccountNumber
                      ? 'Account numbers do not match'
                      : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                    '& .MuiFormHelperText-root': { color: '#E4572E' },
                  }}
                />
                <TextField
                  fullWidth
                  label="IFSC Code"
                  placeholder="e.g. HDFC0001234"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  placeholder="e.g. First Last Name"
                  value={accHolderName}
                  onChange={(e) => setAccHolderName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
                <TextField
                  fullWidth
                  label="UPI ID"
                  placeholder="e.g. user@upi or 9876543210@paytm"
                  value={withdrawTarget}
                  onChange={(e) => setWithdrawTarget(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              fullWidth
              type="submit"
              disabled={withdrawSubmitting}
              variant="contained"
              sx={{
                backgroundColor: '#D4A24C',
                color: '#0F2E2B',
                fontWeight: 700,
                py: 1.2,
                borderRadius: '12px',
                '&:hover': { backgroundColor: '#E8C078' },
              }}
            >
              {withdrawSubmitting ? 'Processing Cashout...' : 'Confirm Instant Withdrawal'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ADD MONEY MODAL */}
      <Dialog
        open={addMoneyModalOpen}
        onClose={() => setAddMoneyModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#153A35',
            color: '#F5F1E6',
            borderRadius: '24px',
            border: '1px solid rgba(245, 241, 230, 0.14)',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            fontFamily: '"Fraunces", serif',
            fontSize: '22px',
          }}
        >
          Add Money to Wallet
          <IconButton onClick={() => setAddMoneyModalOpen(false)} sx={{ color: 'rgba(245, 241, 230, 0.6)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleAddMoneySubmit}>
          <DialogContent>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, mb: 1 }}>Enter Amount (₹)</Typography>
            <TextField
              fullWidth
              placeholder="e.g. 1000"
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Typography sx={{ color: '#D4A24C', fontWeight: 700 }}>₹</Typography></InputAdornment>,
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#0F2E2B',
                  color: '#F5F1E6',
                  borderRadius: '12px',
                  '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                },
              }}
            />

            {/* Quick Amount Selector */}
            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
              {[100, 500, 1000, 2000].map((amt) => (
                <Chip
                  key={amt}
                  label={`+₹${amt}`}
                  onClick={() => setAddAmount(amt.toString())}
                  clickable
                  sx={{
                    backgroundColor: addAmount === amt.toString() ? '#D4A24C' : 'rgba(245, 241, 230, 0.08)',
                    color: addAmount === amt.toString() ? '#0F2E2B' : '#F5F1E6',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(245, 241, 230, 0.7)' }}>Payment Method</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                label="Payment Method"
                sx={{
                  backgroundColor: '#0F2E2B',
                  color: '#F5F1E6',
                  borderRadius: '12px',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                  '.MuiSelect-icon': { color: '#F5F1E6' },
                }}
              >
                <MenuItem value="UPI / GPay">UPI (GPay / PhonePe / Paytm)</MenuItem>
                <MenuItem value="Credit/Debit Card">Credit / Debit Card</MenuItem>
                <MenuItem value="NetBanking">NetBanking</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              fullWidth
              type="submit"
              disabled={addSubmitting}
              variant="contained"
              sx={{
                backgroundColor: '#D4A24C',
                color: '#0F2E2B',
                fontWeight: 700,
                py: 1.2,
                borderRadius: '12px',
                '&:hover': { backgroundColor: '#E8C078' },
              }}
            >
              {addSubmitting ? 'Processing Deposit...' : 'Proceed to Pay & Topup'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DIGITAL RECEIPT MODAL */}
      <Dialog
        open={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#FFFFFF !important',
            backgroundImage: 'none !important',
            color: '#0F2E2B !important',
            borderRadius: '24px !important',
            border: '1px solid rgba(0, 0, 0, 0.08) !important',
            p: 1,
            boxShadow: '0 25px 70px rgba(0,0,0,0.45) !important',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#FFFFFF !important',
              backgroundImage: 'none !important',
              color: '#0F2E2B !important',
              borderRadius: '24px !important',
              border: '1px solid rgba(0, 0, 0, 0.08) !important',
              p: 1,
              boxShadow: '0 25px 70px rgba(0,0,0,0.45) !important',
            },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF !important',
            backgroundImage: 'none !important',
            color: '#0F2E2B !important',
            borderRadius: '24px !important',
            border: '1px solid rgba(0, 0, 0, 0.08) !important',
            p: 1,
            boxShadow: '0 25px 70px rgba(0,0,0,0.45) !important',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            borderBottom: '1px dashed rgba(15, 46, 43, 0.2)',
            pb: 1.5,
            backgroundColor: '#FFFFFF !important',
            color: '#0F2E2B !important',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon sx={{ color: '#1C6758' }} />
            <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: '20px', color: '#0F2E2B' }}>
              Digital Receipt
            </Typography>
          </Box>
          <IconButton onClick={() => setReceiptModalOpen(false)} size="small">
            <CloseIcon sx={{ color: '#0F2E2B' }} />
          </IconButton>
        </DialogTitle>

        {selectedTxn && (
          <DialogContent sx={{ pt: 2.5, backgroundColor: '#FFFFFF !important', color: '#0F2E2B !important' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography sx={{ fontSize: '12px', color: 'rgba(15, 46, 43, 0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                Transaction Amount
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 800,
                  color: selectedTxn.direction === 'CREDIT' ? '#1C6758' : '#E4572E',
                  mt: 0.5,
                }}
              >
                {selectedTxn.direction === 'CREDIT' ? '+' : ''}₹
                {Number(selectedTxn.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
              <Box sx={{ mt: 1 }}>{renderStatusChip(selectedTxn.status)}</Box>
            </Box>

            <Divider sx={{ borderStyle: 'dashed', borderColor: 'rgba(15, 46, 43, 0.2)', my: 2 }} />

            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '13px', color: 'rgba(15, 46, 43, 0.65)' }}>Transaction ID</Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '13px', fontWeight: 700, color: '#0F2E2B' }}>
                  {selectedTxn.id}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '13px', color: 'rgba(15, 46, 43, 0.65)' }}>Title</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0F2E2B', textAlign: 'right' }}>
                  {selectedTxn.title}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '13px', color: 'rgba(15, 46, 43, 0.65)' }}>Reference Code</Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '12px', color: '#1C6758' }}>
                  {selectedTxn.referenceId || selectedTxn.reference}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '13px', color: 'rgba(15, 46, 43, 0.65)' }}>Payment Method</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0F2E2B' }}>
                  {selectedTxn.paymentMethod}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '13px', color: 'rgba(15, 46, 43, 0.65)' }}>Date & Time</Typography>
                <Typography sx={{ fontSize: '12.5px', color: '#0F2E2B' }}>
                  {new Date(selectedTxn.date).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
        )}

        <DialogActions sx={{ p: 2.5, pt: 1, borderTop: '1px dashed rgba(15, 46, 43, 0.2)', backgroundColor: '#FFFFFF !important' }}>
          <Button
            fullWidth
            onClick={() => handleCopy(JSON.stringify(selectedTxn, null, 2), 'receipt')}
            startIcon={<ContentCopyIcon />}
            sx={{
              backgroundColor: '#1C6758',
              color: '#FFFFFF',
              fontWeight: 600,
              borderRadius: '12px',
              py: 1,
              '&:hover': { backgroundColor: '#0F2E2B' },
            }}
          >
            Copy Receipt Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT / ADD PAYMENT ACCOUNT MODAL */}
      <Dialog
        open={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#153A35',
            color: '#F5F1E6',
            borderRadius: '24px',
            border: '1px solid rgba(245, 241, 230, 0.14)',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            fontFamily: '"Fraunces", serif',
            fontSize: '22px',
          }}
        >
          {paymentAccount?.id ? 'Update Payment Account' : 'Add Payment Account'}
          <IconButton onClick={() => setAccountModalOpen(false)} sx={{ color: 'rgba(245, 241, 230, 0.6)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSavePaymentAccountSubmit}>
          <DialogContent>
            <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.6)', mb: 2 }}>
              Configure your primary settlement account details for fast cashback cashouts.
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(245, 241, 230, 0.7)' }}>Account Type</InputLabel>
              <Select
                value={accType}
                onChange={(e) => setAccType(e.target.value)}
                label="Account Type"
                sx={{
                  backgroundColor: '#0F2E2B',
                  color: '#F5F1E6',
                  borderRadius: '12px',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4A24C' },
                  '.MuiSelect-icon': { color: '#F5F1E6' },
                }}
              >
                <MenuItem value="BANK">Bank Account (NEFT/IMPS)</MenuItem>
                <MenuItem value="UPI">UPI Handle</MenuItem>
              </Select>
            </FormControl>

            {accType === 'BANK' ? (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  placeholder="First Last Name"
                  value={accHolderName}
                  onChange={(e) => setAccHolderName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />

                <TextField
                  fullWidth
                  label="Bank Name"
                  placeholder="e.g. Example Bank"
                  value={accBankName}
                  onChange={(e) => setAccBankName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />

                <TextField
                  fullWidth
                  label="Account Number"
                  placeholder="e.g. 1234567890"
                  value={accNumber}
                  onChange={(e) => setAccNumber(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />

                <TextField
                  fullWidth
                  label="IFSC Code"
                  placeholder="e.g. ABCD0123456"
                  value={accIfsc}
                  onChange={(e) => setAccIfsc(e.target.value.toUpperCase())}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
              </Stack>
            ) : (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  placeholder="e.g. First Last Name"
                  value={accHolderName}
                  onChange={(e) => setAccHolderName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />

                <TextField
                  fullWidth
                  label="UPI ID"
                  placeholder="e.g. user@upi or 9876543210@paytm"
                  value={accUpiId}
                  onChange={(e) => setAccUpiId(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(245, 241, 230, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#D4A24C' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(245, 241, 230, 0.6)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#D4A24C' },
                  }}
                />
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              fullWidth
              type="submit"
              disabled={accountSubmitting}
              variant="contained"
              sx={{
                backgroundColor: '#D4A24C',
                color: '#0F2E2B',
                fontWeight: 700,
                py: 1.2,
                borderRadius: '12px',
                '&:hover': { backgroundColor: '#E8C078' },
              }}
            >
              {accountSubmitting
                ? 'Saving Account Details...'
                : paymentAccount?.id
                  ? 'Update Account Details'
                  : 'Add Account Details'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* SNACKBAR / TOAST NOTIFICATION */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 600 }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
